import React, { useRef, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Chip, Grid, Typography } from '@mui/material';
import withLayout from '../../hocs/Layout';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DownloadRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import PageTitle from '../../common/PageTitle';
import PageHero from '../../common/PageHero';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import MUIModal from '../../common/MUIModal';
import { FormProvider, RHFFileField } from '../../common/hook-form';
import { apiConstants, postMultipart, getDownload } from '../../common/constants';

function UploadCard({ title, subtitle, sampleFileName, endpoint, hint, color = 'warning' }) {
    const methods = useForm({ defaultValues: { orderFile: null } });
    const { handleSubmit, reset } = methods;
    const { enqueueSnackbar } = useSnackbar();
    const [isOpen, setIsOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [responseArr, setResponseArr] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [sampleLoading, setSampleLoading] = useState(false);
    const containerRef = useRef(null);

    const resetForm = () => {
        reset({ orderFile: null });
        const fileInput = containerRef.current?.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
    };

    const responseMap = (errors = {}, successes = {}) => {
        const formatted = [];
        Object.keys(errors).forEach((k) => {
            formatted.push({ id: `${title}-err-${k}`, awbNumber: k, status: 'Failed', reason: errors[k] });
        });
        Object.keys(successes).forEach((k) => {
            formatted.push({ id: `${title}-ok-${k}`, awbNumber: k, status: 'Success', reason: successes[k] });
        });
        setResponseArr(formatted);
    };

    const onSubmit = async (data) => {
        if (!data.orderFile?.[0]) {
            enqueueSnackbar('Please choose a file', { variant: 'warning' });
            return;
        }
        setIsUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', data.orderFile[0]);
            const res = await postMultipart(endpoint, fd);
            if (res.status === 200) {
                const { mapErrors, mapSuccess } = res.data || {};
                responseMap(mapErrors, mapSuccess);
                setIsOpen(true);
                resetForm();
                enqueueSnackbar('Upload processed', { variant: 'success' });
            }
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Error uploading file', { variant: 'error' });
        }
        setIsUploading(false);
    };

    const downloadSample = () => {
        setSampleLoading(true);
        getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_FILE}?fileName=${sampleFileName}`)
            .then((res) => {
                const blob = new Blob([res.data], { type: 'application/octet-stream' });
                const href = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', `${sampleFileName}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch(() => enqueueSnackbar('Failed to download sample', { variant: 'error' }))
            .finally(() => setSampleLoading(false));
    };

    const columns = [
        { field: 'awbNumber', headerName: 'AWB', flex: 0.6 },
        {
            field: 'status', headerName: 'Status', flex: 0.5,
            renderCell: ({ row }) => (
                <Chip size='small' label={row.status} color={row.status === 'Success' ? 'success' : 'error'} />
            ),
        },
        {
            field: 'reason', headerName: 'Message', flex: 1.5,
            renderCell: ({ row }) => (
                <Box>
                    {(Array.isArray(row.reason) ? row.reason : [row.reason]).filter(Boolean).map((m, i) => (
                        <Typography key={i} variant='body2'>• {m}</Typography>
                    ))}
                </Box>
            ),
        },
    ];

    return (
        <>
            <Card sx={{ mb: 2 }}>
                <CardHeader title={title} subheader={subtitle} />
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box ref={containerRef}>
                            <RHFFileField name='orderFile' fileType={['xlsx', 'csv']} />
                        </Box>
                        {hint && (
                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>{hint}</Typography>
                        )}
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <LoadingButton type='submit' loading={isUploading} variant='contained' color={color} size='small' startIcon={<FileUploadIcon />}>
                                Upload
                            </LoadingButton>
                            <Button variant='contained' color='error' size='small' onClick={resetForm}>Reset</Button>
                            <LoadingButton loading={sampleLoading} variant='outlined' color='primary' size='small' startIcon={<DownloadRounded />} onClick={downloadSample}>
                                Sample
                            </LoadingButton>
                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            <MUIModal open={isOpen} handleClose={() => setIsOpen(false)} title={<Typography variant='subtitle1'>Upload Details</Typography>}>
                <Grid container>
                    <Grid item xs={12}>
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight
                            rows={responseArr}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            pagination
                            hideFooterPagination={false}
                            paginationMode='client'
                            rowCount={responseArr.length}
                            getRowId={(row) => row.id}
                        />
                    </Grid>
                </Grid>
            </MUIModal>
        </>
    );
}

function BulkUpload() {
    return (
        <div id='main-content' className='container-fluid'>
            <PageTitle subTitle="Schedule or mark missed appointments in bulk">Bulk Upload</PageTitle>
            {/* <PageHero title='Bulk Upload' subtitle='Schedule or mark missed appointments in bulk' sx={{ mb: 2.5 }} /> */}

            {/* <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant='subtitle1' fontWeight={700}>Bulk Tools for Appointment Deliveries</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        Upload CSV/Excel files to schedule or mark missed appointments at scale. Both forms accept the same response format with row-level results.
                    </Typography>
                </CardContent>
            </Card> */}

            <UploadCard
                title='Upload File to Schedule Multiple Appointments'
                subtitle='Schedule appointments for multiple shipments at once'
                sampleFileName='B2B_ABD_BULK'
                endpoint={apiConstants.BUTL_UPLOAD_APPOINTMENT_DELIVERY}
                color='warning'
            />

            <UploadCard
                title='Upload File to Mark Multiple Appointments as Missed'
                subtitle='Close active appointments with reason and remarks in bulk'
                sampleFileName='B2B_ABD_MISS_BULK'
                endpoint={apiConstants.BUTL_UPLOAD_APPOINTMENT_MISS}
                hint='Required columns: awb, reasonCode, remarks, category. Remarks is free text mapped to appointment_del_remarks.'
                color='warning'
            />
        </div>
    );
}

export default withLayout(BulkUpload);
